// Cast & Places' journeys (PlaceDistances.swift): a mapped place's Distance to another — the straight line, worked out
// here — and, for a story set in the past, how long each of its ways of travelling takes. The Mac and iPad also show
// today's routes by Apple Maps (walking, driving, public transport) and measure the period's journeys over today's
// walking route; the web has no Apple Maps, so it says where they are and measures over the straight line with the usual
// detour. Travel in the Story… (`TravelSettingsSheet`) chooses today's transport or the period's ways of travelling, at
// average speeds in km/h or mph as the browser's region measures them.
import { useId, useState } from 'react';
import type { Project } from '@writing-suite/core';
import {
  beforeCars, distanceText, isPeriodTravel, journeyDuration, placeDistance, travelMode, travelTime, usesMiles, DETOUR, MILES_PER_KM, TRAVEL_SUGGESTIONS,
  type TravelMode, type TravelSettings,
} from '@writing-suite/core/story';
import { announce } from '../../shell/context.ts';
import type { LiveProject } from '../../store/project.ts';
import { opened, setTravel, travelOf } from '../../store/story.ts';
import { navigatorLocale, StorySheet } from './common.tsx';

type StoryEntity = ReturnType<typeof opened>['record']['entities'][number];

/** The places a place's Distance can go to: the project's other places on the map. */
export const mappedPlacesBesides = (project: Project, place: StoryEntity): StoryEntity[] =>
  opened(project).record.entities.filter((e) => e.kind === 'place' && e.id !== place.id && e.latitude != null && e.longitude != null);

export function PlaceDistances({ place, others, travel }: { place: StoryEntity; others: StoryEntity[]; travel: TravelSettings }) {
  const id = useId();
  const locale = navigatorLocale();
  const [otherID, setOtherID] = useState<string | null>(null);
  const far = (e: StoryEntity) => placeDistance(place, e) ?? Infinity;
  const sorted = [...others].sort((a, b) => far(a) - far(b));
  const other = others.find((e) => e.id === otherID) ?? sorted[0];
  const straight = other ? placeDistance(place, other) : null;
  if (!other || straight === null) return null;
  const route = straight * DETOUR;
  const modes = travel.periodModes.filter((m) => m.kmh > 0);
  return (
    <>
      <section className="entity-section place-distances" aria-labelledby={`${id}-distance`} data-to={other.id}>
        <h3 id={`${id}-distance`} className="eyebrow">
          Distance
        </h3>
        <div className="field-row">
          <label htmlFor={`${id}-to`}>To</label>
          <select id={`${id}-to`} value={other.id} onChange={(e) => setOtherID(e.target.value)}>
            {sorted.map((e) => (
              <option key={e.id} value={e.id}>
                {e.name || 'Unnamed place'}
              </option>
            ))}
          </select>
        </div>
        <div className="field-row distance-row">
          <span className="field-label">In a straight line</span>
          <span className="distance-value" data-distance="straight">
            {distanceText(straight, locale)}
          </span>
        </div>
      </section>
      <section className="entity-section place-distances" aria-labelledby={`${id}-today`}>
        <h3 id={`${id}-today`} className="eyebrow">
          Today, by Apple Maps
        </h3>
        <p className="apple-note" data-apple-note="Today, by Apple Maps">
          <strong>On your Mac or iPad:</strong> walking, driving and public transport routes and times from Apple Maps, and Show in Maps.
        </p>
      </section>
      {isPeriodTravel(travel) ? (
        <section className="entity-section place-distances" aria-labelledby={`${id}-period`} data-period-journeys={modes.length}>
          <h3 id={`${id}-period`} className="eyebrow">
            In the story’s period
          </h3>
          {modes.map((m) => (
            <div key={m.id} className="field-row distance-row" data-mode-id={m.id}>
              <span className="field-label">{m.name}</span>
              <span className="distance-value">about {journeyDuration(travelTime(route, m.kmh), locale)}</span>
            </div>
          ))}
          <p className="field-note">
            At the speeds set in Travel in the Story…, over the straight line with the usual detour ({distanceText(route, locale)}). On your Mac or iPad, over today’s walking
            route from Apple Maps.
          </p>
        </section>
      ) : null}
    </>
  );
}

/** A speed as the field shows it: km/h or mph, to at most one decimal place. */
const speedText = (kmh: number, miles: boolean, locale: string) =>
  new Intl.NumberFormat(locale, { maximumFractionDigits: 1, useGrouping: false }).format(miles ? kmh * MILES_PER_KM : kmh);

export function TravelSheet({ project, live, canWrite, onClose }: { project: Project; live: LiveProject | null; canWrite: boolean; onClose: () => void }) {
  const locale = navigatorLocale();
  const miles = usesMiles(locale);
  const unit = miles ? 'mph' : 'km/h';
  const [stored] = useState(() => travelOf(project));
  const [modes, setModes] = useState<TravelMode[]>(stored.periodModes);
  /** The modes last set, kept while the story is switched to today, so switching back restores them. */
  const [stashed, setStashed] = useState<TravelMode[]>(() => (isPeriodTravel(stored) ? stored.periodModes : beforeCars().periodModes));
  const [speeds, setSpeeds] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const period = modes.length > 0;
  const choose = (toPeriod: boolean) => {
    if (toPeriod === period) return;
    if (toPeriod) setModes(stashed);
    else {
      setStashed(modes);
      setModes([]);
    }
  };
  const change = (id: string, patch: Partial<TravelMode>) => setModes((all) => all.map((m) => (m.id === id ? { ...m, ...patch } : m)));
  const done = () => {
    if (!live || !canWrite) return onClose();
    const result: TravelSettings = { periodModes: modes.filter((m) => m.name.replace(/^[\t\p{Zs}]+|[\t\p{Zs}]+$/gu, '') !== '' && m.kmh > 0) };
    try {
      if (setTravel(live, result)) announce(isPeriodTravel(result) ? 'Journeys go at the story’s speeds.' : 'Journeys go by today’s transport.');
      onClose();
    } catch (e) {
      setError(`Couldn’t change how people travel in the story: ${e instanceof Error ? e.message : String(e)}`);
    }
  };
  return (
    <StorySheet title="Travel in the Story" onClose={onClose} className="travel-sheet">
      <fieldset className="travel-choice" disabled={!canWrite}>
        <legend className="visually-hidden">Journeys</legend>
        <label>
          <input type="radio" name="journeys" checked={!period} onChange={() => choose(false)} /> By today’s transport
        </label>
        <label>
          <input type="radio" name="journeys" checked={period} onChange={() => choose(true)} /> By the story’s period
        </label>
      </fieldset>
      <p className="field-note">
        Cast &amp; Places shows how long journeys between places take, and Continuity checks that characters have time to make them. Today’s journeys can be by car or aeroplane;
        a story set in the past travels only as fast as the ways listed here.
      </p>
      {period ? (
        <section className="travel-modes" aria-labelledby="travel-modes-title">
          <h3 id="travel-modes-title" className="eyebrow">
            Ways of travelling
          </h3>
          <ul>
            {modes.map((m, i) => (
              <li key={m.id} className="travel-mode" data-mode-id={m.id}>
                <input
                  type="text"
                  aria-label={`Way of travelling ${i + 1}`}
                  placeholder="Way of travelling"
                  value={m.name}
                  disabled={!canWrite}
                  onChange={(e) => change(m.id, { name: e.target.value })}
                />
                <input
                  type="text"
                  inputMode="decimal"
                  className="travel-speed"
                  aria-label={`Speed of ${m.name || `way ${i + 1}`} in ${unit}`}
                  value={speeds[m.id] ?? speedText(m.kmh, miles, locale)}
                  disabled={!canWrite}
                  onChange={(e) => {
                    const text = e.target.value;
                    setSpeeds((s) => ({ ...s, [m.id]: text }));
                    const n = Number(text.trim().replace(',', '.'));
                    if (text.trim() !== '' && Number.isFinite(n)) change(m.id, { kmh: Math.max(0, miles ? n / MILES_PER_KM : n) });
                  }}
                  onBlur={() => setSpeeds(({ [m.id]: _gone, ...rest }) => rest)}
                />
                <span className="travel-unit">{unit}</span>
                {canWrite ? (
                  <button type="button" className="link-button" aria-label={`Remove ${m.name || 'this way of travelling'}`} title="Remove" onClick={() => setModes((all) => all.filter((x) => x.id !== m.id))}>
                    −
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
          {canWrite ? (
            // A list rather than a menu: a menu opens outside the sheet, which a modal dialog makes inert.
            <select
              className="travel-add"
              aria-label="Add a Way of Travelling"
              value=""
              onChange={(e) => {
                const s = TRAVEL_SUGGESTIONS.find((x) => x.name === e.target.value);
                if (s) setModes((all) => [...all, travelMode(s.name, s.kmh)]);
              }}
            >
              <option value="">Add a Way of Travelling…</option>
              {TRAVEL_SUGGESTIONS.map((s) => (
                <option key={s.name} value={s.name}>
                  {s.name}
                </option>
              ))}
            </select>
          ) : null}
          <p className="field-note">Average speeds over a whole journey, stops included.</p>
        </section>
      ) : null}
      {error ? (
        <p className="error" role="alert">
          {error}
        </p>
      ) : null}
      <p className="actions">
        <button type="button" className="button secondary" onClick={onClose}>
          Cancel
        </button>
        <button type="button" className="button primary" onClick={done} disabled={!canWrite}>
          Done
        </button>
      </p>
    </StorySheet>
  );
}
